import { Button } from "@/components/common/Button/Button"
import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { Separator } from "@/components/ui/separator"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { pdfWorkerUrl } from "@/lib/pdfjsWorker"
import { IconMinus, IconPlus, IconX } from "@tabler/icons-react"
import { useEffect, useRef, useState } from "react"
import { Document, Page, pdfjs } from "react-pdf"
import "react-pdf/dist/Page/TextLayer.css"

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl

interface PdfViewerProps {
  fileUrl: string | undefined
  showControls?: boolean
  className?: string
  onClose?: () => void
  initialPage?: number
}

export const PdfViewer: React.FC<PdfViewerProps> = ({
  fileUrl,
  showControls = true,
  className = "",
  onClose,
  initialPage,
}) => {
  const { translate } = useTranslation()
  const [numPages, setNumPages] = useState<number | null>(null)
  const [scale, setScale] = useState(1.0)
  const [containerWidth, setContainerWidth] = useState<number | undefined>(
    undefined,
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const hasScrolledRef = useRef(false)

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth)
      }
    }

    updateWidth()
  }, [])

  useEffect(() => {
    if (
      containerWidth &&
      initialPage !== undefined &&
      !hasScrolledRef.current
    ) {
      setTimeout(() => {
        const pageElement = document.querySelector(
          `[data-page-number="${initialPage}"]`,
        )
        if (pageElement) {
          pageElement.scrollIntoView({ behavior: "smooth", block: "start" })
          hasScrolledRef.current = true
        }
      }, 200)
    }
  }, [initialPage, numPages, containerWidth])

  // useEffect(() => {
  //   // Scroll to the initial page after document loads and pages are rendered with proper dimensions
  //   if (
  //     initialPage &&
  //     numPages &&
  //     containerRef.current &&
  //     containerWidth &&
  //     !hasScrolledRef.current
  //   ) {
  //     const scrollToPage = () => {
  //       const pageElement = document.querySelector(
  //         `[data-page-number="${initialPage}"]`,
  //       ) as HTMLElement

  //       if (pageElement) {
  //         // Check if the page has been properly sized (width is set)
  //         const canvas = pageElement.querySelector("canvas")
  //         if (canvas && canvas.width > 100) {
  //           // Canvas has been rendered with proper dimensions
  //           pageElement.scrollIntoView({ behavior: "smooth", block: "start" })
  //           hasScrolledRef.current = true
  //           return true
  //         }
  //       }
  //       return false
  //     }

  //     // Try to scroll immediately if page is already rendered and sized
  //     if (scrollToPage()) {
  //       return
  //     }

  //     // Otherwise, observe DOM changes to detect when pages are fully rendered
  //     const observer = new MutationObserver(() => {
  //       if (scrollToPage()) {
  //         observer.disconnect()
  //       }
  //     })

  //     observer.observe(containerRef.current, {
  //       childList: true,
  //       subtree: true,
  //       attributes: true,
  //       attributeFilter: ["width", "height"],
  //     })

  //     // Cleanup observer after 5 seconds to prevent memory leaks
  //     const timeout = setTimeout(() => {
  //       observer.disconnect()
  //     }, 5000)

  //     return () => {
  //       observer.disconnect()
  //       clearTimeout(timeout)
  //     }
  //   }
  // }, [initialPage, numPages, containerWidth])

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
  }

  const handleZoomIn = () => {
    if (!containerRef.current) return

    const container = containerRef.current
    const prevScale = scale
    const newScale = Math.min(prevScale + 0.25, 3.0)

    const scrollLeft = container.scrollLeft
    const scrollTop = container.scrollTop
    const centerX = scrollLeft + container.clientWidth / 2
    const centerY = scrollTop + container.clientHeight / 2

    setScale(newScale)

    // Adjust scroll position after scale change
    requestAnimationFrame(() => {
      const scaleRatio = newScale / prevScale
      container.scrollLeft = centerX * scaleRatio - container.clientWidth / 2
      container.scrollTop = centerY * scaleRatio - container.clientHeight / 2
    })
  }

  const handleZoomOut = () => {
    if (!containerRef.current) return

    const container = containerRef.current
    const prevScale = scale
    const newScale = Math.max(prevScale - 0.25, 0.5)

    // Calculate scroll adjustment to maintain center focus
    const scrollLeft = container.scrollLeft
    const scrollTop = container.scrollTop
    const centerX = scrollLeft + container.clientWidth / 2
    const centerY = scrollTop + container.clientHeight / 2

    setScale(newScale)

    // Adjust scroll position after scale change
    requestAnimationFrame(() => {
      const scaleRatio = newScale / prevScale
      container.scrollLeft = centerX * scaleRatio - container.clientWidth / 2
      container.scrollTop = centerY * scaleRatio - container.clientHeight / 2
    })
  }

  return (
    <div className={`flex h-full flex-col ${className}`}>
      {showControls && (
        <div className="mb-4 flex items-center justify-end gap-4">
          <div className="flex flex-row items-center gap-4">
            <Button
              variantSize="small"
              variantColor="dark"
              onClick={handleZoomOut}
              label={translate("PDF_VIEWER/ZOOM_OUT")}
              IconRight={IconMinus}
              isDisabled={scale <= 0.5}
            />
            <span className="text-sm font-medium">
              {Math.round(scale * 100)}%
            </span>
            <Button
              variantSize="small"
              variantColor="dark"
              onClick={handleZoomIn}
              label={translate("PDF_VIEWER/ZOOM_IN")}
              IconRight={IconPlus}
              isDisabled={scale >= 3.0}
            />
          </div>
          <Separator orientation="vertical" />
          {onClose ? (
            <Button
              variantSize="small"
              variantColor="dark"
              onClick={onClose}
              label={translate("CLOSE")}
              IconRight={IconX}
              className="float-right"
            />
          ) : null}
        </div>
      )}

      {/* PDF Viewer - All Pages */}
      <div className="flex-1 overflow-auto" ref={containerRef}>
        <div className="inline-block min-w-full">
          <div className="flex flex-col items-center py-4">
            <Document
              file={fileUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <div className="flex h-full min-h-[400px] items-center justify-center">
                  <LoadingSpinner />
                </div>
              }
              error={
                <div className="text-red-500 flex h-full min-h-[400px] items-center justify-center">
                  {translate("PDF_VIEW_NOT_SUPPORTED")}
                </div>
              }
            >
              {numPages &&
                containerWidth &&
                Array.from(new Array(numPages), (el, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    width={containerWidth - 32}
                    scale={scale}
                    renderTextLayer={true}
                    renderAnnotationLayer={false}
                    className="mb-4 shadow-lg"
                    data-page-number={index + 1}
                  />
                ))}
            </Document>
          </div>
        </div>
      </div>
    </div>
  )
}
